import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import { IconSidebarSimpleLeftSquareFilled } from "@/assets/icons/central/IconSidebarSimpleLeftSquareFilled";
import { withGitpodSize } from "@/assets/icons/utils";
import { Tooltip } from "@/components/Tooltip";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { useExperimentsEnabled, useAnyExperimentVisibleInSettings } from "@/hooks/use-experiment";
import { isTouchDevice } from "@/utils/is-touch-device";
import { ToggleSidebarKeyBinding } from "@/utils/platform";
import { IconSidebarSimpleLeftSquare } from "central-icons/IconSidebarSimpleLeftSquare";
import { IconTestTube } from "central-icons/IconTestTube";
import { useEffect, useState, type FC, type JSX } from "react";
import { Link } from "react-router-dom";
import { TRANSITION_DURATION } from "@/components/SidebarButton.variants";
import { parseAsString, useQueryState } from "nuqs";

const SidebarIconClosed = withGitpodSize(IconSidebarSimpleLeftSquare);

/**
 * Logo size - matches design system h-4 (16px)
 */
const LOGO_SIZE = "h-4";

/**
 * Logo horizontal offset when expanded - 8px (left-2)
 * This aligns the "O" in the wordmark with the centered lettermark
 */
const LOGO_OFFSET = "left-2";

// Helper to create tooltip content
const createTooltipContent = (label: string): JSX.Element => (
    <div className="flex flex-row items-center gap-2">
        <span>{label}</span>
        <KeyboardCombo keys={ToggleSidebarKeyBinding} />
    </div>
);

type SidebarLogoToggleProps = {
    isLocked: boolean;
    onToggle: () => void;
};

export const SidebarLogoToggle: FC<SidebarLogoToggleProps> = ({ isLocked, onToggle }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [showCollapseButton, setShowCollapseButton] = useState(isLocked);
    const isTouch = isTouchDevice();
    const { enabled: experimentsEnabled } = useExperimentsEnabled();
    const { visible: anyVisible } = useAnyExperimentVisibleInSettings();
    const isExperimental = experimentsEnabled && anyVisible;
    const [, setUserSettingsTab] = useQueryState("user-settings", parseAsString);

    // When expanding via keyboard shortcut (not hover), delay collapse button appearance
    // This prevents it from overlapping the logo during the expand animation
    useEffect(() => {
        if (isLocked) {
            // If expand button is not visible (keyboard shortcut used), delay collapse button
            if (!isHovered) {
                // Delay by half the transition duration (100ms) to appear sooner
                const timer = setTimeout(() => {
                    setShowCollapseButton(true);
                }, TRANSITION_DURATION / 2);
                return () => clearTimeout(timer);
            } else {
                // If expand button is visible (hover), show collapse button immediately
                setShowCollapseButton(true);
            }
        } else {
            // When collapsing, hide collapse button immediately
            setShowCollapseButton(false);
        }
    }, [isLocked, isHovered]);

    // Determine what to show based on state
    const showLettermark = !isLocked && !isHovered && !isTouch;
    const showExpandButton = !isLocked && (isTouch || isHovered);

    return (
        <div className="flex items-center justify-between px-2 pb-0 pt-2">
            {/* Logo/Lettermark Container */}
            <div
                className="relative h-8 w-8"
                role="group"
                aria-label="Sidebar logo and expand control"
                onMouseEnter={() => !isTouch && setIsHovered(true)}
                onMouseLeave={() => !isTouch && setIsHovered(false)}
            >
                {/* Full Wordmark - Visible when expanded (instant, no animation) */}
                {isLocked && (
                    <div className={cn("absolute top-0 flex h-8 w-auto items-center gap-2", LOGO_OFFSET)}>
                        <Link
                            to="/ai"
                            className="flex h-8 cursor-pointer items-center"
                            aria-label="Go to home"
                            data-tracking-id="logo-home-link"
                        >
                            <OnaWordmark
                                className={cn(LOGO_SIZE, "w-auto", isExperimental && "text-content-brand-accent-01")}
                            />
                        </Link>
                        {isExperimental && (
                            <button
                                onClick={() => void setUserSettingsTab("experiments")}
                                className="flex h-5 items-center gap-1 rounded bg-surface-brand-accent-01-subtle px-1.5 text-xs font-medium text-content-brand-accent-01 hover:opacity-80"
                                aria-label="Open experiments settings"
                                data-tracking-id="experimental-badge"
                            >
                                <span>Experiments</span>
                                <IconTestTube size={12} />
                            </button>
                        )}
                    </div>
                )}

                {/* Lettermark - Cross-fades with expand button on hover, hidden instantly when expanded */}
                {!isTouch && !isLocked && (
                    <div
                        className={cn(
                            "pointer-events-none absolute left-0 top-0 z-10 flex h-8 w-8 items-center justify-center",
                            "transition-all duration-200",
                            showLettermark ? "scale-100 opacity-100 blur-none" : "scale-50 opacity-0 blur-sm",
                        )}
                        aria-hidden={isHovered}
                    >
                        <OnaLettermark
                            className={cn(LOGO_SIZE, "w-4", isExperimental && "text-content-brand-accent-01")}
                        />
                    </div>
                )}

                {/* Expand Button - Cross-fades with lettermark on hover */}
                {!isLocked && (
                    <Tooltip content={createTooltipContent("Lock sidebar open")} usePortal>
                        <Button
                            variant="ghost"
                            className={cn(
                                "absolute left-0 top-0 h-8 w-8 rounded-[4px] border-0 p-0",
                                "transition-all duration-200",
                                showExpandButton
                                    ? "scale-100 opacity-100 blur-none"
                                    : "pointer-events-none scale-50 opacity-0 blur-sm",
                            )}
                            size="sm"
                            onClick={onToggle}
                            onFocus={() => setIsHovered(true)}
                            onBlur={() => setIsHovered(false)}
                            aria-label="Lock sidebar open"
                            aria-expanded={isLocked}
                            data-tracking-id="toggle-sidebar-expand"
                        >
                            <SidebarIconClosed size="base" />
                        </Button>
                    </Tooltip>
                )}
            </div>

            {/* Collapse Button - Visible when expanded, with delay for keyboard shortcut */}
            {showCollapseButton && (
                <Tooltip content={createTooltipContent("Collapse sidebar")} usePortal>
                    <Button
                        variant="ghost"
                        className={cn(
                            "size-8 rounded-[4px] border-0 p-0 transition-opacity duration-200 ease-out",
                            isLocked ? "opacity-100" : "opacity-0",
                        )}
                        size="sm"
                        onClick={onToggle}
                        aria-label="Collapse sidebar"
                        aria-expanded={isLocked}
                        data-tracking-id="toggle-sidebar-collapse"
                    >
                        <IconSidebarSimpleLeftSquareFilled size="sm" />
                    </Button>
                </Tooltip>
            )}
        </div>
    );
};
